import { debounce } from '@mui/material';
import dayjs from 'dayjs';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ENTITY_ID_PREFIX,
  StatisticsOrigin,
} from '../../../api/hexa/models/EntityStatisticsModels';
import { BusinessBaseType } from '../../../api/business_base/models/BusinessBaseStatisticsModels';

const DEFAULT_REQUESTED_RESULTS = 10;
const MIN_REQUESTED_RESULTS = 1;
const MAX_REQUESTED_RESULTS = 1000;

const defaultStartDate = () => dayjs().subtract(1, 'week').toISOString();
const defaultEndDate = () => dayjs().toISOString();

interface MostSearchedParams<O extends string> {
  start_date?: string;
  end_date?: string;
  requested_results?: number;
  tags?: string[];
  id?: string;
  origin?: O;
  value?: string;
  types?: BusinessBaseType[];
}

export const useMostSearchedStats = <T, O extends string = StatisticsOrigin>(
  fetchResults: (params: MostSearchedParams<O>) => Promise<T[]>,
  onError: (message: string) => void
) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [results, setResults] = useState<T[]>([]);
  const [startDate, setStartDate] = useState<string | null>(
    defaultStartDate()
  );
  const [endDate, setEndDate] = useState<string | null>(defaultEndDate());
  const [requestedResults, setRequestedResults] = useState<number>(
    DEFAULT_REQUESTED_RESULTS
  );
  const [requestedResultsInput, setRequestedResultsInput] = useState<string>(
    String(DEFAULT_REQUESTED_RESULTS)
  );
  const [tags, setTags] = useState<string[]>([]);
  const [origins, setOrigins] = useState<O[]>([]);
  const [entityIdInput, setEntityIdInput] = useState<string>('');
  const [value, setValue] = useState<string>('');
  const [valueInput, setValueInput] = useState<string>('');
  const [types, setTypes] = useState<BusinessBaseType[]>([]);

  /**
   * Rapid filter changes can leave several requests in flight, so only
   * the most recent one is allowed to update state
   */
  const latestRequestId = useRef(0);

  const fetchData = useCallback(async () => {
    const requestId = ++latestRequestId.current;
    const isLatestRequest = () => requestId === latestRequestId.current;
    try {
      setIsLoading(true);
      const results = await fetchResults({
        start_date: startDate ? dayjs(startDate).format('YYYY-MM-DD') : undefined,
        end_date: endDate ? dayjs(endDate).format('YYYY-MM-DD') : undefined,
        requested_results: requestedResults,
        tags: tags.length > 0 ? tags : undefined,
        id: entityIdInput ? `${ENTITY_ID_PREFIX}${entityIdInput}` : undefined,
        // Selecting none or both origins means all origins, so the filter is omitted
        origin: origins.length === 1 ? origins[0] : undefined,
        value: value || undefined,
        types: types.length > 0 ? types : undefined,
      });
      if (!isLatestRequest()) return;
      setResults(results);
    } catch (error: any) {
      if (!isLatestRequest()) return;
      onError(
        `Error ${error.status ?? 500} fetching statistics:
          ${error.response?.data.error ?? 'Error fetching statistics'}`
      );
    } finally {
      if (isLatestRequest()) setIsLoading(false);
    }
  }, [
    fetchResults,
    startDate,
    endDate,
    requestedResults,
    tags,
    origins,
    entityIdInput,
    value,
    types,
    onError,
  ]);

  useEffect(() => {
    fetchData();
    const requestIdRef = latestRequestId;
    return () => {
      // Invalidate the pending request so it can't update state after unmount
      requestIdRef.current++;
    };
  }, [fetchData]);

  /**
   * Debounce the requested results changes so a fetch isn't triggered
   * on every keystroke while the user is typing
   */
  const debouncedSetRequestedResults = useMemo(
    () =>
      debounce((value: number) => {
        setRequestedResults(value);
      }, 500),
    []
  );

  const handleRequestedResultsChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const rawValue = event.target.value;
    setRequestedResultsInput(rawValue);
    const parsedValue = parseInt(rawValue, 10);
    if (isNaN(parsedValue)) return;
    const clampedValue = Math.min(
      Math.max(parsedValue, MIN_REQUESTED_RESULTS),
      MAX_REQUESTED_RESULTS
    );
    debouncedSetRequestedResults(clampedValue);
  };

  const handleEntityIdInputChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setEntityIdInput(event.target.value.replace(/\D/g, ''));
  };

  /**
   * Debounce the value changes so a fetch isn't triggered
   * on every keystroke while the user is typing
   */
  const debouncedSetValue = useMemo(
    () =>
      debounce((value: string) => {
        setValue(value);
      }, 500),
    []
  );

  const handleValueInputChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setValueInput(event.target.value);
    debouncedSetValue(event.target.value.trim());
  };

  const resetFilters = useCallback(() => {
    setStartDate(defaultStartDate());
    setEndDate(defaultEndDate());
    debouncedSetRequestedResults.clear();
    setRequestedResults(DEFAULT_REQUESTED_RESULTS);
    setRequestedResultsInput(String(DEFAULT_REQUESTED_RESULTS));
    setTags([]);
    setOrigins([]);
    setEntityIdInput('');
    debouncedSetValue.clear();
    setValue('');
    setValueInput('');
    setTypes([]);
  }, [debouncedSetRequestedResults, debouncedSetValue]);

  return {
    isLoading,
    results,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    requestedResultsInput,
    handleRequestedResultsChange,
    MIN_REQUESTED_RESULTS,
    MAX_REQUESTED_RESULTS,
    tags,
    setTags,
    origins,
    setOrigins,
    entityIdInput,
    handleEntityIdInputChange,
    valueInput,
    handleValueInputChange,
    types,
    setTypes,
    resetFilters,
  };
};
